import { normalizeVirtualPath } from './virtual-path.js';
import { z } from 'zod';

export const LockResourceSchema = z
  .object({
    target: z
      .string()
      .min(1)
      .max(700)
      .transform((target) => {
        const index = target.indexOf(':file:');
        return index < 0 ? target : target.slice(0, index + 6) + normalizeVirtualPath(target.slice(index + 6));
      }),
    screen: z.enum([
      'game-settings',
      'mods',
      'resources',
      'jvm',
      'software',
      'template',
      'routing',
      'approval',
      'file',
    ]),
  })
  .strict();
export const LockInputSchema = LockResourceSchema.extend({
  token: z.string().uuid().optional(),
  takeover: z.boolean().default(false),
});
export const EditLockSchema = LockResourceSchema.extend({
  token: z.string().uuid(),
  userId: z.string(),
  name: z.string(),
  since: z.string(),
  expiresAt: z.string(),
});
export type LockResource = z.infer<typeof LockResourceSchema>;
export type EditLock = z.infer<typeof EditLockSchema>;
export const ContentHashSchema = z.string().regex(/^[0-9a-f]{64}$/);
export const RevisionSchema = z.string().min(1).max(100);

export function operationResource(kind: string, serverId: string): LockResource | undefined {
  const screens: Record<string, LockResource['screen']> = {
    change_game_settings: 'game-settings',
    change_mods: 'mods',
    change_modpack: 'mods',
    change_resources: 'resources',
    change_jvm_args: 'jvm',
    change_software: 'software',
    change_version: 'software',
  };
  const screen = screens[kind];
  return screen ? { target: `server:${serverId}`, screen } : undefined;
}

/** Shared route mapping keeps Web preconditions and REST guards aligned. */
export function routeResource(path: string, body?: unknown): LockResource | undefined {
  const url = new URL(path, 'http://localhost');
  const parts = url.pathname
    .replace(/^\/api\/v1/, '')
    .split('/')
    .filter(Boolean)
    .map(decodeURIComponent);
  const value = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
  if (parts[0] === 'requests') {
    if (parts[1]) return { target: `request:${parts[1]}`, screen: 'approval' };
    if (typeof value.kind === 'string' && typeof value.serverId === 'string')
      return operationResource(value.kind, value.serverId);
  }
  if (parts[0] === 'velocity' && parts[2] === 'preview') return undefined;
  // Power actions do not take the edit lock, like the start/stop buttons of world servers.
  if (parts[0] === 'velocity' && ['start', 'stop', 'restart'].includes(parts[1] ?? '')) return undefined;
  if (parts[0] === 'velocity') return { target: 'velocity', screen: 'mods' };
  if (parts[0] === 'routing') return { target: 'routing', screen: 'routing' };
  if (['templates', 'client-mod-templates'].includes(parts[0] ?? '') && parts[1])
    return { target: `${parts[0]}:${parts[1]}`, screen: 'template' };
  if (parts[0] !== 'servers' || !parts[1]) return undefined;
  const target = `server:${parts[1]}`;
  const screens: Record<string, LockResource['screen']> = {
    'game-settings': 'game-settings',
    mods: 'mods',
    pack: 'mods',
    'launcher-policy': 'mods',
    'client-mod-template': 'software',
    launch: 'jvm',
    resources: 'resources',
  };
  if (parts[2] === 'files') {
    const file = typeof value.path === 'string' ? value.path : url.searchParams.get('path');
    return file ? { target: `${target}:file:${normalizeVirtualPath(file)}`, screen: 'file' } : undefined;
  }
  const screen = screens[parts[2] ?? ''];
  return screen ? { target, screen } : undefined;
}
